import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {TechnicalDeviceNamespace} from '@namespaces/technical-device.namespace';
import {DynamicFields} from '@namespaces/dynamic-fields.namespace';
import {SelectValueInterface} from '@interfaces/select-value.interface';
import {untilDestroyed} from 'ngx-take-until-destroy';
import {TechnicalDeviceApiService} from '@api/technical-device.api.service';
import {distinctUntilChanged, filter, takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {TechnicalDeviceElementTypeApiService} from '@api/technical-device-element-type.api.service';
import {ActivatedRoute} from '@angular/router';
import {TechnicalDocumentsService} from '@providers/technical-documents/technical-documents.service';

@Injectable()
export class ElementGroupFormService implements OnDestroy {

    /**
     * Значения для списка типов элемента, параметров типа и значения выбранного типа элемента
     */
    public typesList$ = new BehaviorSubject<TechnicalDeviceNamespace.ElementGroupTypeInterface[]>([]);
    public typeParams$ = new BehaviorSubject<DynamicFields.Field[]>([]);
    public typeLoaded$ = new BehaviorSubject<boolean>(false);
    private typeIdValue$ = new BehaviorSubject<number|string>(null);

    /**
     * Значения для списка НТД для элемента, параметров НТД и значения выбранного НТД для элемента
     */
    public ntdList$ = new BehaviorSubject<SelectValueInterface[]>([]);
    public ntdParams$ = new BehaviorSubject<DynamicFields.Field[]>([]);
    private ntdIdValue$ = new BehaviorSubject<number>(null);
    private typesUpdated$ = new Subject();

    public methodsList$ = new BehaviorSubject<SelectValueInterface[]>([]);

    private deviceId: number;

    constructor(
        private api: TechnicalDeviceApiService,
        private typesApi: TechnicalDeviceElementTypeApiService,
        private route: ActivatedRoute,
        private ntdApi: TechnicalDocumentsService,
    ) {}

    init(deviceId: number) {
        this.deviceId = deviceId;

        const ntdList = this.route.snapshot.data.ntdList;
        const ntdParams = this.route.snapshot.data.ntdParams;
        const typesList = this.route.snapshot.data.typesList;
        const typeParams = this.route.snapshot.data.typeParams;
        const techCardsList = this.route.snapshot.data.techCardsList;

        this.ntdList$.next(ntdList || []);

        /**
         * Либо сейчас редактирвоание и все параметры были получены из роутинга,
         * либо сейчас создание нового типоразмера, поэтому нужна реакция на изменения полей
         */
        if (ntdParams && typesList && typeParams && techCardsList) {
            // Если редактирование
            this.ntdParams$.next(ntdParams);
            this.typesList$.next(typesList);
            this.typeParams$.next(typeParams);
            this.methodsList$.next(techCardsList);
            this.typeLoaded$.next(true);
        } else {
            // Если новая форма
            this.ntdIdValue$.pipe(
                distinctUntilChanged(),
                untilDestroyed(this),
            ).subscribe((ntdId) => {
                this.updateNtdParams(ntdId);
                this.updateTypesList(ntdId);
                this.updateMethodsList(ntdId);
            });
        }
    }

    /**
     * Принять изменение выбранного типа элемента
     * @param typeId
     */
    handleTypeChange(typeId: number|string) {
        this.typeIdValue$.next(typeId);
    }

    /**
     * Принять изменения выбранной НТД
     * @param ntdId
     */
    handleNtdChange(ntdId: number) {
        this.ntdIdValue$.next(ntdId);
    }

    private updateMethodsList(ntdId: number): void {
        if (!ntdId) {
            return;
        }

        this.ntdApi.get({
            url: this.ntdApi.getMethodsPath(ntdId),
        }).subscribe((methods: SelectValueInterface[]) => {
            this.methodsList$.next(methods || []);
        }, () => {
            this.methodsList$.next([]);
        });
    }

    /**
     * Обновить список доступных типов элементов
     */
    private updateTypesList(ntdId: number): void {
        if (!ntdId) {
            return;
        }

        this.typeLoaded$.next(false);

        this.api.get({
            url: this.api.getElementsGroupTypesPath(this.deviceId, ntdId),
        }).subscribe((types: TechnicalDeviceNamespace.ElementGroupTypeInterface[]) => {
            this.typesList$.next(types || []);
            this.typeLoaded$.next(true);
            this.typesUpdated$.next();

            this.typeIdValue$.pipe(
                distinctUntilChanged(),
                takeUntil(this.typesUpdated$),
                untilDestroyed(this),
            ).subscribe((typeId) => {
                this.updateTypeParams(Number(typeId));
            });
        }, () => {
            this.typesList$.next([]);
            this.typeLoaded$.next(true);
            this.typesUpdated$.next();
        });
    }

    /**
     * Обновить список динамических параметров, зависящих от НТД
     * @param ntdId
     */
    private updateNtdParams(ntdId: number) {
        if (!ntdId) {
            this.ntdParams$.next([]);
            return;
        }

        this.api.get({
            url: this.api.getStandardTechnicalDocumentParamsPath(this.deviceId, ntdId),
        }).subscribe((params) => {
            this.ntdParams$.next(params || []);
        }, () => {
            this.ntdParams$.next([]);
        });
    }

    /**
     * Обновить список динамических параметров, зависящих от типа элемента
     * @param typeId
     */
    private updateTypeParams(typeId: number) {
        const ntdId = this.ntdIdValue$.getValue();
        if (!typeId || !ntdId) {
            this.typeParams$.next([]);
            return;
        }

        this.typesApi.get({
            url: this.typesApi.getParamsPath(typeId, ntdId),
        }).subscribe((params) => {
            this.typeParams$.next(params || []);
        }, () => {
            this.typeParams$.next([]);
        });
    }

    ngOnDestroy() {
        /**
         * Завершаем все потоки
         */
        this.typesList$.complete();
        this.typeParams$.complete();
        this.typeIdValue$.complete();
        this.typeLoaded$.complete();
        this.ntdList$.complete();
        this.ntdParams$.complete();
        this.ntdIdValue$.complete();
        this.typesUpdated$.complete();
    }
}
